import React, { useState } from 'react';
import {
    Card,
    Table,
    Space,
    Typography,
    Select,
    DatePicker,
    Input,
    Button,
    Row,
    Col,
    Modal,
    message,
    Tag,
    Tooltip,
} from 'antd';
import {
    SafetyCertificateOutlined,
    StopOutlined,
    CheckCircleOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs, { Dayjs } from 'dayjs';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import {
    useGetFlaggedResponsesQuery,
    useBulkRejectFlaggedResponsesMutation,
    useBulkConfirmCleanFlaggedResponsesMutation,
    useBulkRescoreFlaggedResponsesMutation,
    useGetAdminBrandsQuery,
} from '../../../services/nestjs-base-api';
import { BulkRejectDialog, BulkRejectMode } from '../../components/response-integrity/BulkRejectDialog';
import { StatusBadge } from '../../components/response-integrity/StatusBadge';
import { QualityScoreDisplay } from '../../components/response-integrity/QualityScoreDisplay';
import { LlmCostPanel } from '../../components/response-integrity/LlmCostPanel';
import { ResponseQuickView } from '../../components/response-integrity/ResponseQuickView';
import { detectorName } from '../../components/response-integrity/integrityI18n';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface FiredDetector {
    detectorId: string;
    contributionScore: number;
    weight: number;
    confidence: number;
}

interface FlaggedRow {
    id: number;
    surveyId: number;
    surveyName: string;
    brandId: number | null;
    brandName: string | null;
    organisationId: number | null;
    cohortName: string | null;
    countryCode: string | null;
    submittedAt: string;
    qualityScore: number | null;
    qualityTier: 'clean' | 'suspect' | 'flagged' | 'quarantined' | null;
    rejectionStatus: 'flagged' | 'rejected' | 'confirmed_clean' | null;
    panelPartner: string | null;
    detectorsFiredCount: number;
    detectorsFired?: FiredDetector[];
    participantEmail: string | null;
}

export const ReviewerDashboardPage: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const surveyIdParam = searchParams.get('surveyId');
    const brandIdParam = searchParams.get('brandId');
    const surveyId = surveyIdParam ? Number(surveyIdParam) : undefined;
    const brandId = brandIdParam ? Number(brandIdParam) : undefined;

    const [tier, setTier] = useState<string | undefined>(undefined);
    const [status, setStatus] = useState<string | undefined>('flagged');
    const [brandIdFilter, setBrandIdFilter] = useState<number | undefined>(brandId);
    const [panelPartner, setPanelPartner] = useState<string | undefined>(undefined);
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [bulkRejectOpen, setBulkRejectOpen] = useState(false);

    const [bulkReject, { isLoading: bulkRejecting }] = useBulkRejectFlaggedResponsesMutation();
    const [bulkConfirmClean, { isLoading: bulkConfirming }] = useBulkConfirmCleanFlaggedResponsesMutation();
    const [bulkRescore, { isLoading: bulkRescoring }] = useBulkRescoreFlaggedResponsesMutation();

    const { data, isLoading, isFetching } = useGetFlaggedResponsesQuery({
        jwtToken: user?.jwtToken || '',
        surveyId,
        brandId: brandIdFilter,
        tier,
        rejectionStatus: status,
        panelPartner: panelPartner || undefined,
        dateFrom: dateRange?.[0]?.toISOString(),
        dateTo: dateRange?.[1]?.toISOString(),
        page,
        limit,
    });

    const { data: brandsData } = useGetAdminBrandsQuery(
        { jwtToken: user?.jwtToken || '', limit: 1000 },
        { skip: !user?.jwtToken },
    );
    const brandOptions = (brandsData?.data ?? [])
        .map((b: any) => ({
            value: b.id,
            label: b.organisationName ? `${b.name} (${b.organisationName})` : b.name,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

    const rows: FlaggedRow[] = data?.data ?? [];
    const total: number = data?.total ?? 0;

    const handleResetFilters = () => {
        setTier(undefined);
        setStatus('flagged');
        setBrandIdFilter(brandId);
        setPanelPartner(undefined);
        setDateRange(null);
        setPage(1);
    };

    const reportBulkOutcome = (
        succeeded: number,
        failed: number,
        successKey: string,
    ) => {
        if (failed === 0) {
            message.success(intl.formatMessage({ id: successKey }, { count: succeeded }));
        } else {
            message.warning(
                intl.formatMessage(
                    { id: 'responseIntegrity.bulk.toast.partial' },
                    { succeeded, failed },
                ),
            );
        }
    };

    const handleBulkReject = async (
        mode: BulkRejectMode,
        reason?: string,
        notes?: string,
    ) => {
        try {
            const r: any = await bulkReject({
                jwtToken: user?.jwtToken || '',
                ids: selectedIds,
                mode,
                reason,
                notes,
            }).unwrap();
            reportBulkOutcome(
                r?.succeeded ?? 0,
                r?.failed ?? 0,
                'responseIntegrity.bulk.toast.rejected',
            );
            setBulkRejectOpen(false);
            setSelectedIds([]);
        } catch (e) {
            message.error(intl.formatMessage({ id: 'responseIntegrity.toast.error' }));
        }
    };

    const handleBulkConfirmClean = () => {
        Modal.confirm({
            title: intl.formatMessage(
                { id: 'responseIntegrity.bulk.confirmCleanTitle' },
                { count: selectedIds.length },
            ),
            content: intl.formatMessage({ id: 'responseIntegrity.bulk.confirmCleanBody' }),
            okText: intl.formatMessage({ id: 'responseIntegrity.actions.confirmClean' }),
            cancelText: intl.formatMessage({ id: 'common.cancelButton' }),
            onOk: async () => {
                try {
                    const r: any = await bulkConfirmClean({
                        jwtToken: user?.jwtToken || '',
                        ids: selectedIds,
                    }).unwrap();
                    reportBulkOutcome(
                        r?.succeeded ?? 0,
                        r?.failed ?? 0,
                        'responseIntegrity.bulk.toast.confirmedClean',
                    );
                    setSelectedIds([]);
                } catch (e) {
                    message.error(intl.formatMessage({ id: 'responseIntegrity.toast.error' }));
                }
            },
        });
    };

    const handleBulkRescore = () => {
        Modal.confirm({
            title: intl.formatMessage(
                { id: 'responseIntegrity.bulk.rescoreTitle' },
                { count: selectedIds.length },
            ),
            content: intl.formatMessage({ id: 'responseIntegrity.bulk.rescoreBody' }),
            okText: intl.formatMessage({ id: 'responseIntegrity.actions.rescore' }),
            cancelText: intl.formatMessage({ id: 'common.cancelButton' }),
            onOk: async () => {
                try {
                    const r: any = await bulkRescore({
                        jwtToken: user?.jwtToken || '',
                        ids: selectedIds,
                    }).unwrap();
                    reportBulkOutcome(
                        r?.succeeded ?? 0,
                        r?.failed ?? 0,
                        'responseIntegrity.bulk.toast.rescored',
                    );
                    setSelectedIds([]);
                } catch (e) {
                    message.error(intl.formatMessage({ id: 'responseIntegrity.toast.error' }));
                }
            },
        });
    };

    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div>
                    <Title level={3} style={{ margin: 0 }}>
                        <SafetyCertificateOutlined style={{ marginRight: 8 }} />
                        <FormattedMessage id="responseIntegrity.dashboard.title" />
                    </Title>
                    <Text type="secondary">
                        <FormattedMessage id="responseIntegrity.dashboard.subtitle" />
                    </Text>
                </div>

                <Card>
                    <Row gutter={[12, 12]} align="middle">
                        <Col xs={24} sm={12} md={8}>
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                style={{ width: '100%' }}
                                placeholder={intl.formatMessage({
                                    id: 'responseIntegrity.filters.brand',
                                })}
                                value={brandIdFilter}
                                onChange={(v) => {
                                    setBrandIdFilter(v);
                                    setPage(1);
                                }}
                                options={brandOptions}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Select
                                allowClear
                                style={{ width: '100%' }}
                                placeholder={intl.formatMessage({
                                    id: 'responseIntegrity.filters.tier',
                                })}
                                value={tier}
                                onChange={(v) => {
                                    setTier(v);
                                    setPage(1);
                                }}
                                options={[
                                    { value: 'flagged', label: intl.formatMessage({ id: 'responseIntegrity.tier.flagged' }) },
                                    { value: 'quarantined', label: intl.formatMessage({ id: 'responseIntegrity.tier.quarantined' }) },
                                    { value: 'suspect', label: intl.formatMessage({ id: 'responseIntegrity.tier.suspect' }) },
                                    { value: 'clean', label: intl.formatMessage({ id: 'responseIntegrity.tier.clean' }) },
                                ]}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Select
                                style={{ width: '100%' }}
                                placeholder={intl.formatMessage({
                                    id: 'responseIntegrity.filters.status',
                                })}
                                value={status}
                                onChange={(v) => {
                                    setStatus(v);
                                    setPage(1);
                                }}
                                options={[
                                    { value: 'flagged', label: intl.formatMessage({ id: 'responseIntegrity.status.unreviewed' }) },
                                    { value: 'rejected', label: intl.formatMessage({ id: 'responseIntegrity.status.rejected' }) },
                                    { value: 'confirmed_clean', label: intl.formatMessage({ id: 'responseIntegrity.status.confirmedClean' }) },
                                    { value: 'all', label: intl.formatMessage({ id: 'responseIntegrity.status.all' }) },
                                ]}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <RangePicker
                                style={{ width: '100%' }}
                                value={dateRange ?? undefined}
                                onChange={(v) => {
                                    setDateRange(v as [Dayjs | null, Dayjs | null] | null);
                                    setPage(1);
                                }}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Input
                                placeholder={intl.formatMessage({
                                    id: 'responseIntegrity.filters.panelPartner',
                                })}
                                value={panelPartner ?? ''}
                                onChange={(e) => {
                                    setPanelPartner(e.target.value || undefined);
                                    setPage(1);
                                }}
                            />
                        </Col>
                        <Col xs={24}>
                            <Space>
                                <Button onClick={handleResetFilters}>
                                    <FormattedMessage id="common.clearSelection" />
                                </Button>
                                <Text type="secondary">
                                    <FormattedMessage
                                        id="responseIntegrity.dashboard.totalCount"
                                        values={{ count: total }}
                                    />
                                </Text>
                            </Space>
                        </Col>
                    </Row>
                </Card>

                {selectedIds.length > 0 && (
                    <Card styles={{ body: { padding: '12px 16px' } }}>
                        <Space wrap>
                            <Text strong>
                                <FormattedMessage
                                    id="responseIntegrity.bulk.toolbar.selected"
                                    values={{ count: selectedIds.length }}
                                />
                            </Text>
                            <Button
                                danger
                                icon={<StopOutlined />}
                                onClick={() => setBulkRejectOpen(true)}
                                loading={bulkRejecting}
                            >
                                <FormattedMessage id="responseIntegrity.actions.reject" />
                            </Button>
                            <Button
                                icon={<CheckCircleOutlined />}
                                onClick={handleBulkConfirmClean}
                                loading={bulkConfirming}
                            >
                                <FormattedMessage id="responseIntegrity.actions.confirmClean" />
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={handleBulkRescore}
                                loading={bulkRescoring}
                            >
                                <FormattedMessage id="responseIntegrity.actions.rescore" />
                            </Button>
                            <Button type="link" onClick={() => setSelectedIds([])}>
                                <FormattedMessage id="responseIntegrity.bulk.toolbar.clear" />
                            </Button>
                        </Space>
                    </Card>
                )}

                <Card styles={{ body: { padding: 0 } }}>
                    <Table<FlaggedRow>
                        rowKey="id"
                        loading={isLoading || isFetching}
                        dataSource={rows}
                        rowSelection={{
                            selectedRowKeys: selectedIds,
                            onChange: (keys) => setSelectedIds(keys.map((k) => Number(k))),
                            preserveSelectedRowKeys: true,
                        }}
                        expandable={{
                            expandedRowRender: (row) => (
                                <ResponseQuickView responseId={row.id} />
                            ),
                        }}
                        pagination={{
                            current: page,
                            pageSize: limit,
                            total,
                            showSizeChanger: true,
                            pageSizeOptions: [25, 50, 100],
                            onChange: (p, ps) => {
                                setPage(p);
                                setLimit(ps);
                            },
                        }}
                        locale={{
                            emptyText: intl.formatMessage({
                                id: 'responseIntegrity.dashboard.empty',
                            }),
                        }}
                        columns={[
                            {
                                title: <FormattedMessage id="responseIntegrity.table.qualityScore" />,
                                dataIndex: 'qualityScore',
                                width: 160,
                                render: (_v, row) => (
                                    <QualityScoreDisplay
                                        score={row.qualityScore}
                                        tier={row.qualityTier}
                                    />
                                ),
                            },
                            {
                                title: <FormattedMessage id="responseIntegrity.table.status" />,
                                dataIndex: 'rejectionStatus',
                                width: 140,
                                render: (s) => <StatusBadge status={s} />,
                            },
                            {
                                title: <FormattedMessage id="responseIntegrity.table.survey" />,
                                dataIndex: 'surveyName',
                                render: (name: string, row) => (
                                    <div>
                                        <div>{name || `#${row.surveyId}`}</div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {[
                                                row.brandName,
                                                row.cohortName,
                                                row.countryCode,
                                            ]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </Text>
                                    </div>
                                ),
                            },
                            {
                                title: <FormattedMessage id="responseIntegrity.table.submittedAt" />,
                                dataIndex: 'submittedAt',
                                width: 160,
                                render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
                            },
                            {
                                title: <FormattedMessage id="responseIntegrity.table.panelPartner" />,
                                dataIndex: 'panelPartner',
                                width: 120,
                                render: (v: string | null) =>
                                    v || <Text type="secondary">—</Text>,
                            },
                            {
                                title: <FormattedMessage id="responseIntegrity.table.detectorsFired" />,
                                dataIndex: 'detectorsFired',
                                width: 280,
                                render: (fired: FiredDetector[] | undefined, row) => {
                                    if (!fired || fired.length === 0) {
                                        return row.detectorsFiredCount > 0 ? (
                                            row.detectorsFiredCount
                                        ) : (
                                            <Text type="secondary">—</Text>
                                        );
                                    }
                                    const visible = fired.slice(0, 3);
                                    const overflow = fired.slice(3);
                                    return (
                                        <Space size={[4, 4]} wrap>
                                            {visible.map((d) => (
                                                <Tooltip
                                                    key={d.detectorId}
                                                    title={`-${Number(d.contributionScore).toFixed(1)}`}
                                                >
                                                    <Tag style={{ margin: 0 }}>
                                                        {detectorName(intl, d.detectorId)}
                                                    </Tag>
                                                </Tooltip>
                                            ))}
                                            {overflow.length > 0 && (
                                                <Tooltip
                                                    title={overflow
                                                        .map(
                                                            (d) =>
                                                                `${detectorName(intl, d.detectorId)} (-${Number(d.contributionScore).toFixed(1)})`,
                                                        )
                                                        .join(', ')}
                                                >
                                                    <Tag style={{ margin: 0 }}>
                                                        +{overflow.length}
                                                    </Tag>
                                                </Tooltip>
                                            )}
                                        </Space>
                                    );
                                },
                            },
                            {
                                title: <FormattedMessage id="responseIntegrity.table.actions" />,
                                key: 'actions',
                                width: 100,
                                render: (_v, row) => (
                                    <Link to={`/admin/response-integrity/responses/${row.id}`}>
                                        <FormattedMessage id="responseIntegrity.actions.view" />
                                    </Link>
                                ),
                            },
                        ]}
                    />
                </Card>
            </Space>

            <div style={{ padding: '0 24px 24px' }}>
                <LlmCostPanel />
            </div>

            <BulkRejectDialog
                open={bulkRejectOpen}
                submitting={bulkRejecting}
                selectedCount={selectedIds.length}
                onCancel={() => setBulkRejectOpen(false)}
                onSubmit={handleBulkReject}
            />
        </div>
    );
};
